import { APP_MODULE, AppModule } from '@app/auth/models/session.model';

export const SIDENAV_GROUP = {
  OPERATION: 'operation',
  ADMIN: 'admin',
} as const;

export type SidenavGroup = (typeof SIDENAV_GROUP)[keyof typeof SIDENAV_GROUP];

export const SIDENAV_GROUP_LABEL: Record<SidenavGroup, string> = {
  [SIDENAV_GROUP.OPERATION]: 'Operación',
  [SIDENAV_GROUP.ADMIN]: 'Administración',
};

export const SIDENAV_GROUP_ORDER: SidenavGroup[] = [SIDENAV_GROUP.OPERATION, SIDENAV_GROUP.ADMIN];

export interface TreeRouteInfoModel {
  path: string;
  title: string;
  icon: string;
  group?: SidenavGroup;
  needModule?: AppModule | AppModule[];
  disableShow?: boolean;
  alwaysVisible?: boolean;
  children: TreeRouteInfoModel[];
}

export interface SidenavNavGroup {
  key: SidenavGroup;
  label: string;
  items: TreeRouteInfoModel[];
}

export const SIDENAV_ROUTE_MAP: TreeRouteInfoModel[] = [
  {
    path: 'dashboard',
    group: SIDENAV_GROUP.OPERATION,
    title: 'Dashboard',
    icon: 'grid_view',
    needModule: APP_MODULE.DASHBOARD,
    children: [],
  },
  {
    path: 'cedulas',
    group: SIDENAV_GROUP.OPERATION,
    title: 'Cédulas',
    icon: 'assignment',
    needModule: APP_MODULE.SCHEDULES,
    children: [],
  },
  {
    path: 'entregas',
    group: SIDENAV_GROUP.OPERATION,
    title: 'Entregas',
    icon: 'local_shipping',
    needModule: APP_MODULE.DELIVERIES,
    children: [],
  },
  {
    path: 'incidencias',
    group: SIDENAV_GROUP.OPERATION,
    title: 'Incidencias',
    icon: 'report_problem',
    needModule: APP_MODULE.INCIDENTS,
    children: [],
  },
  {
    path: 'bitacora-caseta',
    group: SIDENAV_GROUP.OPERATION,
    title: 'Bitácora de caseta',
    icon: 'security',
    needModule: APP_MODULE.GATE_LOG,
    children: [],
  },
  {
    path: 'usuarios',
    group: SIDENAV_GROUP.ADMIN,
    title: 'Usuarios',
    icon: 'group',
    needModule: APP_MODULE.USERS,
    children: [],
  },
  {
    path: 'roles',
    group: SIDENAV_GROUP.ADMIN,
    title: 'Roles',
    icon: 'admin_panel_settings',
    needModule: APP_MODULE.ROLES,
    children: [],
  },
  {
    path: 'rastreo',
    title: 'Rastreo',
    icon: 'travel_explore',
    disableShow: true,
    children: [],
  },
  {
    path: 'reportes',
    title: 'Reportes',
    icon: 'description',
    disableShow: true,
    alwaysVisible: true,
    children: [],
  },
  {
    path: 'rutas',
    title: 'Rutas',
    icon: 'route',
    disableShow: true,
    alwaysVisible: true,
    children: [],
  },
  {
    path: 'repartidores',
    title: 'Repartidores',
    icon: 'person_pin_circle',
    disableShow: true,
    alwaysVisible: true,
    children: [],
  },
  {
    path: 'configuracion',
    group: SIDENAV_GROUP.ADMIN,
    title: 'Configuración',
    icon: 'settings',
    needModule: [
      APP_MODULE.DELIVERY_NONCONFORMITY_TYPES,
      APP_MODULE.OUTPUT_WAREHOUSES,
      APP_MODULE.VEHICLES,
      APP_MODULE.AREAS,
    ],
    children: [
      {
        path: 'tipos-no-conforme',
        title: 'Tipos de no conforme para entregas',
        icon: 'report_gmailerrorred',
        needModule: APP_MODULE.DELIVERY_NONCONFORMITY_TYPES,
        children: [],
      },
      {
        path: 'almacenes-salida',
        title: 'Almacenes de salida',
        icon: 'warehouse',
        needModule: APP_MODULE.OUTPUT_WAREHOUSES,
        children: [],
      },
      {
        path: 'vehiculos',
        title: 'Vehículos',
        icon: 'directions_car',
        needModule: APP_MODULE.VEHICLES,
        children: [],
      },
      {
        path: 'areas',
        title: 'Áreas',
        icon: 'workspaces',
        needModule: APP_MODULE.AREAS,
        children: [],
      },
    ],
  },
];

export const SIDENAV_SETTINGS_PATH = 'configuracion';

export function getRoutesSidenav(): TreeRouteInfoModel[] {
  return buildAbsoluteTree(SIDENAV_ROUTE_MAP, '');
}

export function getFirstAccessibleModulePath(
  hasModuleAccess: (module: AppModule) => boolean,
): string | null {
  const match = SIDENAV_ROUTE_MAP.find(
    (route) => !route.disableShow && hasAnyModuleAccess(route.needModule, hasModuleAccess),
  );

  return match?.path ?? null;
}

export function hasAnyModuleAccess(
  needModule: AppModule | AppModule[] | undefined,
  hasModuleAccess: (module: AppModule) => boolean,
): boolean {
  if (needModule === undefined) return false;
  const required = Array.isArray(needModule) ? needModule : [needModule];
  return required.some((module) => hasModuleAccess(module));
}

function buildAbsoluteTree(routes: TreeRouteInfoModel[], basePath: string): TreeRouteInfoModel[] {
  return routes.map((route) => {
    const absolutePath = route.path ? `${basePath}/${route.path}` : basePath || '/';
    return {
      ...route,
      path: absolutePath,
      children: buildAbsoluteTree(route.children, absolutePath),
    };
  });
}
